import React from 'react';
import { Play, Pause, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PlaybackControlsProps {
  isPaused: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Reusable playback controls component with play/pause and stop buttons.
 * Used on Dashboard, AudioCommentsPage, and anywhere else playback controls are needed.
 */
const PlaybackControls: React.FC<PlaybackControlsProps> = ({
  isPaused,
  onPlay,
  onPause,
  onStop,
  size = 'md',
  className = '',
}) => {
  const { t } = useTranslation();

  // Size configurations
  const sizeConfig = {
    sm: { button: 'w-8 h-8', icon: 'w-4 h-4' },
    md: { button: 'w-10 h-10', icon: 'w-5 h-5' },
    lg: { button: 'w-12 h-12', icon: 'w-6 h-6' },
  };

  const { button: buttonSize, icon: iconSize } = sizeConfig[size];

  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      {isPaused ? (
        <button
          onClick={onPlay}
          className={`${buttonSize} flex items-center justify-center rounded-full border-2 transition-all hover:scale-110 active:scale-95`}
          style={{ 
            backgroundColor: 'var(--color-surface)', 
            borderColor: 'var(--brand-from)',
            color: 'var(--brand-from)'
          }}
          title={t('track.play')}
        >
          <Play className={iconSize} />
        </button>
      ) : (
        <button
          onClick={onPause}
          className={`${buttonSize} flex items-center justify-center rounded-full border-2 transition-all hover:scale-110 active:scale-95`}
          style={{ 
            backgroundColor: 'var(--color-surface)', 
            borderColor: 'var(--brand-from)',
            color: 'var(--brand-from)'
          }}
          title={t('track.pause')}
        >
          <Pause className={iconSize} />
        </button>
      )}
      <button
        onClick={onStop}
        className={`${buttonSize} flex items-center justify-center rounded-full border-2 transition-all hover:scale-110 active:scale-95 hover:bg-[var(--color-hover)]`}
        style={{ 
          backgroundColor: 'var(--color-surface)', 
          borderColor: 'var(--color-border)',
          color: 'var(--color-text)'
        }}
        title={t('track.stop')}
      >
        <Square className={iconSize} />
      </button>
    </div>
  );
};

export default PlaybackControls;

